/**
 * AC-M5-7 — "Use as…" ⋮ menu on transcript messages
 *
 * Shown on assistant message bubbles as a small ⋮ button.
 * Opens a dropdown with: "Use as Goal", "Use as Outcome", "Use as Knowledge Doc"
 * On selection, calls POST /:id/promote → shows PromotionResultDialog
 */
import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Target, TrendingUp, BookOpen, Loader2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import type { PromotionDraft } from '@/services/roros/agent-sessions.service';

// ─── PromotionResultDialog ────────────────────────────────────────────────────
// Simple inline dialog shown after "Use as..." — shows the pre-filled draft
// so the user can copy & paste into the Goal/Outcome/KnowledgeDoc create flow.

interface PromotionResultDialogProps {
  draft: PromotionDraft;
  draftType: 'goal' | 'outcome' | 'knowledge_doc';
  onClose: () => void;
}

export function PromotionResultDialog({ draft, draftType, onClose }: PromotionResultDialogProps) {
  const labelMap: Record<string, string> = {
    goal: t('Goal'),
    outcome: t('Outcome'),
    knowledge_doc: t('Knowledge Doc'),
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      data-testid="promotion-result-dialog"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h3 className="font-semibold text-slate-800 text-base mb-3">
          {t('Draft')} {labelMap[draftType] ?? draftType}
        </h3>
        {draft.title && (
          <div className="mb-2">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('Title')}</label>
            <p className="text-sm text-slate-800 mt-0.5 bg-slate-50 rounded px-2 py-1 border border-slate-200" data-testid="promotion-draft-title">
              {draft.title}
            </p>
          </div>
        )}
        {draft.summary && (
          <div className="mb-2">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('Summary')}</label>
            <p className="text-sm text-slate-700 mt-0.5 bg-slate-50 rounded px-2 py-1 border border-slate-200 whitespace-pre-wrap" data-testid="promotion-draft-summary">
              {draft.summary}
            </p>
          </div>
        )}
        {draft.description && (
          <div className="mb-2">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('Description')}</label>
            <p className="text-sm text-slate-700 mt-0.5 bg-slate-50 rounded px-2 py-1 border border-slate-200 whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="promotion-draft-description">
              {draft.description}
            </p>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3 mb-4">
          {t('Copy the content above and paste it when creating your')} {labelMap[draftType] ?? draftType}.
        </p>
        <Button onClick={onClose} size="sm" className="w-full" data-testid="promotion-result-close">
          {t('Done')}
        </Button>
      </div>
    </div>
  );
}

// ─── UseAsMenu ────────────────────────────────────────────────────────────────

interface UseAsMenuProps {
  sessionId: string;
  messageId: string;
}

export function UseAsMenu({ sessionId, messageId }: UseAsMenuProps) {
  const [open, setOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotionResult, setPromotionResult] = useState<{
    draft: PromotionDraft;
    draftType: 'goal' | 'outcome' | 'knowledge_doc';
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function handlePromote(targetType: 'goal' | 'outcome' | 'knowledge_doc') {
    setOpen(false);
    setPromoting(true);
    try {
      const result = await agentSessionsService.promoteMessage(sessionId, messageId, targetType);
      setPromotionResult({ draft: result.draft ?? {}, draftType: result.draftType });
    } catch {
      // non-fatal — silently ignore if promote fails
    } finally {
      setPromoting(false);
    }
  }

  const menuItems: { key: 'goal' | 'outcome' | 'knowledge_doc'; label: string; icon: React.ElementType }[] = [
    { key: 'goal',          label: t('Use as Goal'),         icon: Target },
    { key: 'outcome',       label: t('Use as Outcome'),      icon: TrendingUp },
    { key: 'knowledge_doc', label: t('Use as Knowledge Doc'), icon: BookOpen },
  ];

  return (
    <>
      <div className="relative inline-block" ref={menuRef}>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600"
          onClick={() => setOpen((v) => !v)}
          aria-label={t('Use as…')}
          data-testid="use-as-menu-trigger"
          disabled={promoting}
        >
          {promoting
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <MoreHorizontal className="h-3.5 w-3.5" />
          }
        </Button>

        {open && (
          <div
            className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-30 py-1 overflow-hidden"
            data-testid="use-as-menu-dropdown"
          >
            <p className="px-3 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wide border-b border-slate-100">
              {t('Use as…')}
            </p>
            {menuItems.map(({ key, label, icon: Icon }) => (
              <Button
                key={key}
                variant="ghost"
                className="w-full flex items-center gap-2 px-3 py-2 h-auto text-sm text-slate-700 hover:bg-slate-50 active:bg-slate-50 hover:text-slate-700 transition-colors text-left whitespace-normal"
                onClick={() => void handlePromote(key)}
                data-testid={`use-as-${key}`}
              >
                <Icon className="h-3.5 w-3.5 text-slate-500" />
                {label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {promotionResult && (
        <PromotionResultDialog
          draft={promotionResult.draft}
          draftType={promotionResult.draftType}
          onClose={() => setPromotionResult(null)}
        />
      )}
    </>
  );
}
